import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminListUsers } from '../../lib/adminApi';
import type { AdminUserRow, UserRole } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import SegmentedFilter from '../../components/Admin/SegmentedFilter';

/*
 * Velaigal /admin/users — the admin user directory.
 *
 * Role filter + on-submit search both reset to page 1 (adminListUsers is
 * offset-paginated via page/hasMore, not a cursor — see src/lib/adminApi.ts).
 * Rows are read-only; disable/enable/delete all live one level down on
 * UserDetail (/admin/users/:id), which this list only links into.
 */

type RoleFilter = UserRole | 'all';

const ROLE_FILTERS: Array<{ value: RoleFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'jobseeker', label: 'Job seekers' },
  { value: 'hr', label: 'Recruiters' },
  { value: 'admin', label: 'Admins' },
];

const ROLE_LABEL: Record<UserRole, string> = {
  jobseeker: 'Job seeker',
  hr: 'Recruiter',
  admin: 'Admin',
};

// Role and Disabled chips are both status-only, never the accent state, so
// both get the neutral hairline override — saffron stays reserved for a
// genuinely "live"/positive signal (see StatusChip in src/pages/HR/MyJobs.tsx).
const NEUTRAL_CHIP_STYLE = { backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' } as const;

const RoleChip: React.FC<{ role: UserRole }> = ({ role }) => (
  <span className="chip" style={NEUTRAL_CHIP_STYLE}>
    {ROLE_LABEL[role]}
  </span>
);

const DisabledChip: React.FC = () => (
  <span className="chip" style={NEUTRAL_CHIP_STYLE}>
    Disabled
  </span>
);

const LoadingBlock: React.FC = () => (
  <div role="status" className="py-16 text-center">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-6 text-sm text-ink-tertiary">Loading users.</p>
  </div>
);

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">Couldn&apos;t load users.</h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const EmptyBlock: React.FC = () => (
  <div className="bg-paper border border-hairline rounded-md p-8 text-center">
    <p className="text-ink">No users match this filter.</p>
  </div>
);

const UserRow: React.FC<{ user: AdminUserRow }> = ({ user }) => (
  <Link to={`/admin/users/${user.id}`} className="card block no-underline text-ink">
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <RoleChip role={user.role} />
          {user.disabledAt && <DisabledChip />}
        </div>
        <p className="font-display text-lg font-medium text-ink">{user.name}</p>
        <p className="text-sm text-ink-secondary mt-0.5">{user.email}</p>
      </div>
      <span className="text-xs text-ink-tertiary flex-shrink-0">Joined {formatRelativeDate(user.createdAt)}</span>
    </div>
  </Link>
);

const Users: React.FC = () => {
  const [role, setRole] = useState<RoleFilter>('all');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — mirrors the load effect in src/pages/HR/MyJobs.tsx.
  // Re-runs whenever role/q/page change, in addition to manual retries.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await adminListUsers({ role: role === 'all' ? undefined : role, q: q || undefined, page });
        if (cancelled) return;
        setUsers(res.users);
        setHasMore(res.hasMore);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [role, q, page, loadNonce]);

  const handleRoleChange = (next: RoleFilter) => {
    setRole(next);
    setPage(1);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setQ(qInput.trim());
    setPage(1);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">Admin</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">Users</h1>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <SegmentedFilter options={ROLE_FILTERS} value={role} onChange={handleRoleChange} ariaLabel="Filter by role" />
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
          <input
            type="text"
            className="input"
            placeholder="Search name or email"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            style={{ maxWidth: 240 }}
            aria-label="Search users"
          />
          <button type="submit" className="btn-secondary" style={{ padding: '0.5rem 1rem' }}>
            Search
          </button>
        </form>
      </div>

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBlock message={error} onRetry={() => setLoadNonce((n) => n + 1)} />
      ) : users.length === 0 ? (
        <EmptyBlock />
      ) : (
        <>
          <div className="space-y-4">
            {users.map((u) => (
              <UserRow key={u.id} user={u} />
            ))}
          </div>

          <div className="flex items-center justify-between mt-8">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="text-xs text-ink-tertiary">Page {page}</span>
            <button type="button" className="btn-secondary" onClick={() => setPage((p) => p + 1)} disabled={!hasMore}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Users;
